import { AxiosError } from 'axios';
import { ApiHostKey, resolveApiHost } from '../http/apiHosts';

/**
 * How much a single HTTP failure tells us about the session.
 *
 * - `certain` — this failure only happens when the token is rejected. Act on it.
 * - `probe`   — ambiguous. Ask the server before doing anything drastic.
 * - `ignore`  — tells us nothing about the session. Never act, never probe.
 */
export type Suspicion = 'ignore' | 'probe' | 'certain';

export interface FailureContext {
  suspicion: Suspicion;
  host: ApiHostKey | null;
  status: number | null;
  code: string | null;
}

/**
 * Sign-in itself. A 401 here means "wrong password", not "your session
 * expired" — treating it as the latter would pop an expiry notice on the login
 * page for anyone who fat-fingers their credentials.
 */
const isLoginRequest = (url: string) => /\/v1\/auth\b/.test(url);

export const classifyFailure = (error: AxiosError): FailureContext => {
  const host = resolveApiHost(error?.config);
  const status = error?.response?.status ?? null;
  const body = error?.response?.data as { code?: unknown } | undefined;
  const code = typeof body?.code === 'string' ? body.code : null;

  const context: FailureContext = { suspicion: 'ignore', host, status, code };

  // No response at all: network down, CORS, timeout, or a cancelled request.
  // Core being unreachable is not a dead session, and probing in this state is
  // how you get a probe storm during an outage.
  if (!error?.response) {
    return context;
  }

  // Unrecognised host. The interceptor is installed on the shared default axios
  // instance, so this also covers third-party traffic.
  if (!host) {
    return context;
  }

  const requestUrl = `${error.config?.baseURL ?? ''}${error.config?.url ?? ''}`;
  if (isLoginRequest(requestUrl)) {
    return context;
  }

  if (status === 401) {
    // Either the explicit contract from appointments-api, or Core v4's
    // `auth_bearer_for_api`. Both only fire for a rejected token.
    return { ...context, suspicion: 'certain' };
  }

  if (status === 403) {
    // Genuinely ambiguous. Core v2's `auth_bearer_for_user` answers 403 for a
    // dead token, but 403 is also ordinary RBAC — see the permission-denied
    // handling in components/rpms/claims/RPMClaims.tsx. Must never be `certain`.
    return { ...context, suspicion: 'probe' };
  }

  if (status === 404 && host === 'auth') {
    // Core v1's `set_user` renders 404 when no user matches the auth_token.
    return { ...context, suspicion: 'probe' };
  }

  if (status === 500 && host === 'appointments') {
    // Before the appointments-api classification fix is deployed, a dead token
    // arrives here as a bare 500. Once it is deployed this path stops firing,
    // because the same failure becomes a 401.
    return { ...context, suspicion: 'probe' };
  }

  // 502/503 from appointments-api are the explicit "Core is unhealthy, retry"
  // signals, and every other 5xx is an unrelated backend bug. Probing on those
  // would hammer a struggling backend.
  return context;
};
